/**
 * RBAC API – AuthService RbacController
 * GET /api/v1/rbac/roles (GetRolesWithPermissions)
 * Uses same origin as Auth API (VITE_AUTH_SERVICE_URL, e.g. https://localhost:7043).
 * AllowAnonymous – no token required.
 */

import axios, { AxiosInstance } from 'axios';
import { AUTH_API_CONFIG } from '../config/api.config';

/** Matches AuthService.Application.DTOs.RoleWithPermissionsDto */
export interface RoleWithPermissionsDto {
  id: number;
  code: string;
  name: string | null;
  description: string | null;
  permissionCodes: string[];
}

const getBaseUrl = (): string => {
  const url = AUTH_API_CONFIG.baseUrl?.trim();
  if (url) {
    try {
      return new URL(url).origin;
    } catch {
      return url;
    }
  }
  return import.meta.env.DEV ? '' : 'https://auth-api.azure-api.net';
};

const rbacClient: AxiosInstance = axios.create({
  baseURL: getBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: AUTH_API_CONFIG.timeout,
});

const ROLES_PATH = '/api/v1/rbac/roles';

/** Normalize role from API (handles camelCase or PascalCase from .NET). */
function normalizeRole(r: Record<string, unknown>): RoleWithPermissionsDto {
  return {
    id: (r.id ?? r.Id) as number,
    code: ((r.code ?? r.Code) as string) ?? '',
    name: (r.name ?? r.Name) as string | null ?? null,
    description: (r.description ?? r.Description) as string | null ?? null,
    permissionCodes: Array.isArray(r.permissionCodes ?? r.PermissionCodes)
      ? (r.permissionCodes ?? r.PermissionCodes) as string[]
      : [],
  };
}

/**
 * Get all roles with their permission codes.
 * Caching is handled by the caller (rolesCache).
 */
export async function getRolesWithPermissions(): Promise<RoleWithPermissionsDto[]> {
  const { data } = await rbacClient.get<unknown>(ROLES_PATH);
  if (!Array.isArray(data)) {
    throw new Error('RBAC roles API did not return an array');
  }
  return data.map((r) => normalizeRole(r as Record<string, unknown>));
}

export const RbacApi = {
  getRolesWithPermissions,
};
